/* The floating text-edit overlay, label editing, and Fusion-style in-place
   editing of dimensions and constraint badges on the canvas. */
import type { Constraint, LabelItem, Pt, Wall } from './types.ts';
import { $, hooks, pushUndo, state } from './state.ts';
import { parseLen, plain, uid } from './utils.ts';
import { wallClear, wallVec } from './geom.ts';
import { clampOpenings, shiftWallEnd } from './weld.ts';
import { solveConstraints } from './solver.ts';

const textEdit = $('#textEdit') as HTMLInputElement;
let editingLabel: ((v: string) => void) | null = null;

export function openTextEdit(sp: Pt, initial: string, cb: (v: string) => void): void {
  textEdit.style.display = 'block';
  textEdit.style.left = sp.x + 'px'; textEdit.style.top = (sp.y - 14) + 'px';
  textEdit.value = initial;
  textEdit.focus(); textEdit.select();
  editingLabel = cb;
}
textEdit.addEventListener('keydown', e => {
  if (e.key === 'Enter') { commitTextEdit(); }
  if (e.key === 'Escape') { textEdit.style.display = 'none'; editingLabel = null; }
  e.stopPropagation();
});
textEdit.addEventListener('blur', commitTextEdit);
function commitTextEdit(): void {
  if (!editingLabel) return;
  const cb = editingLabel; editingLabel = null;
  textEdit.style.display = 'none';
  cb(textEdit.value.trim());
}

export function editNewLabel(pt: Pt, sp: Pt): void {
  openTextEdit(sp, '', v => {
    if (v) {
      pushUndo();
      const l: LabelItem = { id: uid('l'), x: pt.x, y: pt.y, text: v, size: 8 };
      state.doc.labels.push(l);
      state.sel = new Set([l.id]);
      hooks.markDirty(); hooks.renderPanel();
    }
    hooks.setTool('select');
  });
}
export function editLabel(l: LabelItem, sp: Pt): void {
  openTextEdit(sp, l.text, v => {
    pushUndo();
    if (v) l.text = v;
    else state.doc.labels = state.doc.labels.filter(x => x !== l);
    hooks.markDirty(); hooks.renderPanel();
  });
}
export function editFixtureLabel(f: { label?: string }, sp: Pt): void {
  openTextEdit(sp, f.label || '', v => { pushUndo(); f.label = v; hooks.markDirty(); hooks.renderPanel(); });
}

/* ---- in-place dimension / constraint editing ---- */
const inHit = (sp: Pt, h: import('./types.ts').Hit | null | undefined) => {
  if (!h) return false;
  const dx = sp.x - h.x, dy = sp.y - h.y;
  const c = Math.cos(-h.ang), s = Math.sin(-h.ang);
  return Math.abs(dx * c - dy * s) <= h.hw && Math.abs(dx * s + dy * c) <= h.hh;
};

export function overTie(sp: Pt): Constraint | null {
  for (const c of (state.doc.constraints || [])) {
    if (inHit(sp, c._hit)) return c;
    if (c.type === 'angle' && inHit(sp, c._hit2)) return c;
  }
  return null;
}
export function overDim(sp: Pt): { wall: Wall; which: 'len' | 'clear' } | null {
  for (const wl of state.doc.walls) {
    if (inHit(sp, wl._hitClear)) return { wall: wl, which: 'clear' };
    if (inHit(sp, wl._hitLen)) return { wall: wl, which: 'len' };
  }
  return null;
}

export function removeConstraint(c: Constraint): void {
  pushUndo();
  state.doc.constraints = (state.doc.constraints || []).filter(x => x !== c);
  hooks.markDirty(); hooks.renderPanel();
}

export const CONFLICT_MSG = "That value can't be reached — it conflicts with other constraints or locked walls, so it was left unchanged.";

/** Click on a constraint badge: edit its value in place; clearing removes it.
    A value the sketch can't satisfy is rejected and reverted (Fusion-style). */
export function editConstraint(c: Constraint, sp: Pt): void {
  if (c.type === 'dist') {
    const A = state.doc.walls.find(w => w.id === c.a), B = state.doc.walls.find(w => w.id === c.b);
    if (!A || !B) return;
    openTextEdit(sp, plain(Math.abs(c.dist) - A.th / 2 - B.th / 2), v => {
      if (!v) return removeConstraint(c);
      const nv = parseLen(v, 'in');
      if (nv === null || nv <= 0) return;
      pushUndo();
      const prev = c.dist;
      c.dist = (c.dist < 0 ? -1 : 1) * (nv + A.th / 2 + B.th / 2);
      if (!solveConstraints(state.doc, new Set([A.id]), [c.a, c.b]).ok) {
        c.dist = prev; hooks.notify(CONFLICT_MSG);
      }
      clampOpenings(state.doc); hooks.markDirty(); hooks.renderPanel();
    });
  } else if (c.type === 'angle') {
    openTextEdit(sp, String(c.deg), v => {
      if (!v) return removeConstraint(c);
      const nv = parseFloat(v.replace(/[°˚]/g, ''));
      if (isNaN(nv)) return;
      pushUndo();
      const prev = c.deg;
      c.deg = ((nv % 180) + 180) % 180;
      // wall a is the datum: it stays put, only b (and its weld chain) rotates
      if (!solveConstraints(state.doc, new Set([c.a]), [c.a, c.b]).ok) {
        c.deg = prev; hooks.notify(CONFLICT_MSG);
      }
      clampOpenings(state.doc); hooks.markDirty(); hooks.renderPanel();
    });
  } else if (c.type === 'axis') {
    openTextEdit(sp, c.axis.toUpperCase(), v => {
      if (!v) return removeConstraint(c);
      const ax = v.trim().toLowerCase()[0];
      if (ax !== 'h' && ax !== 'v') return;
      pushUndo();
      const prev = c.axis;
      c.axis = ax;
      if (!solveConstraints(state.doc, new Set(), [c.a]).ok) {
        c.axis = prev; hooks.notify(CONFLICT_MSG);
      }
      clampOpenings(state.doc); hooks.markDirty(); hooks.renderPanel();
    });
  } else {
    // valueless constraints: the badge just asks whether to remove the tie
    const q = c.type === 'coin' ? 'Detach this wall end from the wall it’s tied to?'
      : c.type === 'mid' ? 'Remove the midpoint tie? The end will no longer stay centered on the other wall.'
      : c.type === 'colin' ? 'Remove the collinear constraint? The walls will no longer stay on one line.'
      : c.type === 'fix' ? 'Unfix this wall so it can move again?'
      : 'Remove the equal-length constraint?';
    hooks.confirm(q, c.type === 'fix' ? 'Unfix' : 'Detach')
      .then(yes => { if (yes) removeConstraint(c); });
  }
}

/** Click on a wall dimension: type an exact centerline length or clear span. */
export function editWallDim(wl: Wall, which: 'len' | 'clear', sp: Pt): void {
  if (wl.locked) return;
  const v2 = wallVec(wl);
  const eaten = which === 'clear' ? v2.len - wallClear(state.doc.walls, wl) : 0;
  const cur = which === 'clear' ? wallClear(state.doc.walls, wl) : v2.len;
  openTextEdit(sp, plain(cur), v => {
    const nv = parseLen(v, 'in');
    if (nv === null || nv <= 0) return;
    pushUndo();
    if (!shiftWallEnd(state.doc, wl, v2.ux * (nv + eaten - v2.len), v2.uy * (nv + eaten - v2.len)))
      hooks.notify(CONFLICT_MSG);
    clampOpenings(state.doc); hooks.markDirty(); hooks.renderPanel();
  });
}
